import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import socket from "../socket";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  setConnected,
  setConnecting,
  setRoomCreated,
  setRoomJoined,
  setError,
  resetMultiplayer,
  selectMpError,
  selectMpIsConnecting,
  selectMpRole,
  selectMultiplayer,
  RoomState,
} from "../store/multiplayerSlice";
import { useFarcaster } from "../contexts/FarcasterContext";
import PlayerAvatar from "../components/multiplayer/PlayerAvatar";
import ShareInviteBtn from "../components/multiplayer/ShareInviteBtn";
import { SmallButton } from "../components/UI/smallButton/SmallButton";
import { getPlayerId } from "../helpers/identity";
import { media } from "../styles/GlobalStyles";

// ─── Styled ───────────────────────────────────────────────────────────────────

const JoinTypeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
`;

const JoinTypeBtn = styled.button<{ active?: boolean }>`
  padding: 1rem 0.8rem;
  border: 2px solid
    ${({ active }) => (active ? "var(--color-purple)" : "rgba(0,0,0,0.18)")};
  border-radius: 1.2rem;
  background: ${({ active }) =>
    active ? "rgba(121,69,255,0.12)" : "transparent"};
  color: ${({ active }) =>
    active ? "var(--color-dark-purple)" : "rgba(0,0,0,0.5)"};
  font-size: 1.3rem;
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: var(--color-purple);
    color: var(--color-dark-purple);
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #1a0533 0%, #5c2dd5 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled(motion.div)`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow: 0 1rem 0 var(--color-black);
  border-radius: 2rem;
  padding: 3.2rem;
  width: 100%;
  max-width: 44rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${media.phone} {
    padding: 2rem;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  font-size: var(--font-size-heading-m);
  text-transform: uppercase;
  color: var(--color-black);
`;

const FarcasterUserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: rgba(121, 69, 255, 0.08);
  border: 1px solid rgba(121, 69, 255, 0.3);
  border-radius: 10rem;
  padding: 0.4rem 1rem 0.4rem 0.4rem;
`;

const FarcasterUserName = styled.span`
  font-size: 1.3rem;
  font-weight: bold;
  color: var(--color-dark-purple);
`;

const Subtitle = styled.h2`
  font-size: var(--font-size-heading-xs);
  text-transform: uppercase;
  color: var(--color-black);
  margin-bottom: 0.4rem;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1.2rem 1.6rem;
  border: 2px solid var(--color-black);
  border-radius: 1.2rem;
  font-size: var(--font-size-heading-xs);
  font-family: inherit;
  outline: none;
  background: rgba(0, 0, 0, 0.04);
  text-transform: uppercase;
  letter-spacing: 0.1em;

  &:focus {
    border-color: var(--color-purple);
    box-shadow: 0 0 0 3px rgba(121, 69, 255, 0.2);
  }
  &::placeholder {
    text-transform: none;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.3);
  }
`;

const NameInput = styled(Input)`
  text-transform: none;
  letter-spacing: normal;
`;

const ActionBtn = styled.button<{ color?: "purple" | "red" | "yellow" }>`
  width: 100%;
  padding: 1.4rem 1.6rem;
  border: 3px solid var(--color-black);
  box-shadow: 0 0.8rem 0 var(--color-black);
  border-radius: 1.6rem;
  background: ${({ color }) =>
    color === "red"
      ? "var(--color-red)"
      : color === "yellow"
        ? "var(--color-yellow)"
        : "var(--color-purple)"};
  color: ${({ color }) =>
    color === "yellow" ? "var(--color-black)" : "var(--color-white)"};
  font-size: var(--font-size-heading-xs);
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    box-shadow: 0 0.8rem 0 var(--color-dark-purple);
  }
  &:active {
    transform: translateY(3px);
    box-shadow: 0 5px 0 var(--color-dark-purple);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CreatedRoomBox = styled.div`
  background: linear-gradient(
    135deg,
    rgba(121, 69, 255, 0.1),
    rgba(92, 45, 213, 0.05)
  );
  border: 2px solid rgba(121, 69, 255, 0.3);
  border-radius: 1.2rem;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const CreatedLabel = styled.p`
  font-size: 1.3rem;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CreatedCode = styled.p`
  font-size: 3.2rem;
  font-weight: bold;
  color: var(--color-dark-purple);
  letter-spacing: 0.3em;
  font-family: monospace;
`;

const WaitText = styled.p`
  font-size: 1.3rem;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const CopyCodeBtn = styled.button`
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.8rem;
  padding: 0.4rem 1.2rem;
  font-size: 1.2rem;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const BackBtn = styled(SmallButton)`
  margin-top: 1.6rem;
`;

const ErrorMsg = styled.p`
  color: var(--color-red);
  font-size: 1.4rem;
  text-align: center;
  background: rgba(253, 102, 135, 0.12);
  border-radius: 0.8rem;
  padding: 0.8rem 1.2rem;
`;

const StatusMsg = styled.p`
  color: var(--color-purple);
  font-size: 1.4rem;
  text-align: center;
`;

// ─── Rooms List Styled Components ────────────────────────────────────────────

const RoomsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RoomsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-height: 28rem;
  overflow-y: auto;
  padding-right: 0.4rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 0.4rem;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(121, 69, 255, 0.3);
    border-radius: 2rem;
  }
`;

const RoomCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.4rem;
  background: rgba(121, 69, 255, 0.05);
  border: 1.5px solid rgba(121, 69, 255, 0.2);
  border-radius: 1.2rem;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: rgba(121, 69, 255, 0.4);
    background: rgba(121, 69, 255, 0.09);
  }
`;

const RoomCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const RoomCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const RoomCardHost = styled.p`
  font-size: 1.4rem;
  font-weight: bold;
  color: var(--color-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoomCardCode = styled.span`
  font-size: 1.1rem;
  color: var(--color-dark-purple);
  font-family: monospace;
  letter-spacing: 0.1em;
  font-weight: bold;
`;

const RoomCardMeta = styled.p`
  font-size: 1.2rem;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const RoomStatusBadge = styled.span<{
  status: "waiting" | "playing" | "finished";
}>`
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.6rem;
  border-radius: 0.6rem;
  background: ${({ status }) =>
    status === "waiting"
      ? "rgba(99, 195, 99, 0.15)"
      : status === "playing"
        ? "rgba(121, 69, 255, 0.15)"
        : "rgba(0,0,0,0.08)"};
  color: ${({ status }) =>
    status === "waiting"
      ? "#2a8a2a"
      : status === "playing"
        ? "var(--color-dark-purple)"
        : "rgba(0,0,0,0.4)"};
`;

const RoomCardActions = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
`;

const RoomJoinBtn = styled.button<{ variant: "join" | "spectate" }>`
  padding: 0.5rem 1rem;
  border: 2px solid var(--color-black);
  box-shadow: 0 0.3rem 0 var(--color-black);
  border-radius: 0.8rem;
  background: ${({ variant }) =>
    variant === "join" ? "var(--color-purple)" : "var(--color-yellow)"};
  color: ${({ variant }) =>
    variant === "join" ? "var(--color-white)" : "var(--color-black)"};
  font-size: 1.1rem;
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    box-shadow: 0 0.3rem 0 var(--color-dark-purple);
  }
  &:active {
    transform: translateY(2px);
    box-shadow: none;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyRoomsMsg = styled.p`
  font-size: 1.3rem;
  color: rgba(0, 0, 0, 0.4);
  text-align: center;
  padding: 1.6rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 1rem;
`;

const RefreshBtn = styled.button`
  background: transparent;
  border: 1.5px solid rgba(121, 69, 255, 0.3);
  border-radius: 0.8rem;
  padding: 0.3rem 0.8rem;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--color-dark-purple);
  font-family: inherit;
  transition: all 0.15s;
  &:hover {
    border-color: var(--color-purple);
    background: rgba(121, 69, 255, 0.06);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface RoomSummary {
  id: string;
  hostName: string;
  hostPfpUrl?: string;
  hostUsername?: string;
  hostFid?: number;
  playerCount: number;
  spectatorCount: number;
  isStarted: boolean;
  winner: string | null;
  scores: { p1: number; p2: number };
}

const MultiplayerLobby: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user: farcasterUser } = useFarcaster();

  const error = useAppSelector(selectMpError);
  const isConnecting = useAppSelector(selectMpIsConnecting);
  const role = useAppSelector(selectMpRole);
  const mpState = useAppSelector(selectMultiplayer);

  const [playerName, setPlayerName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinAs, setJoinAs] = useState<"player" | "spectator">("player");
  const [socketReady, setSocketReady] = useState(socket.connected);
  const [copiedCode, setCopiedCode] = useState(false);
  const [availableRooms, setAvailableRooms] = useState<RoomSummary[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Auto-fill name from Farcaster user context
  useEffect(() => {
    if (farcasterUser) {
      setPlayerName(farcasterUser.displayName || farcasterUser.username || "");
    }
  }, [farcasterUser]);

  // Fetch available rooms from REST API
  const fetchRooms = async () => {
    setLoadingRooms(true);
    try {
      const SERVER_URL =
        process.env.REACT_APP_SOCKET_URL ||
        (process.env.NODE_ENV === "production" ? "" : "http://localhost:3001");
      const res = await fetch(`${SERVER_URL}/api/rooms`);
      if (res.ok) {
        const data = await res.json();
        setAvailableRooms(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[rooms] Failed to fetch rooms:", err);
      }
      // Silently fail — rooms list is non-critical
    } finally {
      setLoadingRooms(false);
    }
  };

  // Connect socket on mount
  useEffect(() => {
    dispatch(resetMultiplayer());
    if (!socket.connected) socket.connect();
    fetchRooms();

    const onConnect = () => {
      setSocketReady(true);
      dispatch(setConnected(true));
    };
    const onDisconnect = () => {
      setSocketReady(false);
      dispatch(setConnected(false));
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected) {
      setSocketReady(true);
      dispatch(setConnected(true));
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Listen for server broadcasts to keep the rooms list fresh in real-time.
  // FIX C2: accept BOTH event names (server emits both for compatibility).
  useEffect(() => {
    const onRoomsUpdated = (list: RoomSummary[]) => {
      setAvailableRooms(list);
    };
    socket.on("rooms-updated", onRoomsUpdated);
    socket.on("rooms-list", onRoomsUpdated);
    return () => {
      socket.off("rooms-updated", onRoomsUpdated);
      socket.off("rooms-list", onRoomsUpdated);
    };
  }, []);

  // Listen for server responses
  useEffect(() => {
    const onRoomCreated = (data: {
      roomId: string;
      role: "p1";
      color: "red";
      roomState: RoomState;
    }) => {
      dispatch(setRoomCreated(data));
    };
    const onRoomJoined = (data: {
      roomId: string;
      role: "p2" | "spectator";
      color: "yellow" | null;
      roomState: RoomState;
    }) => {
      dispatch(setRoomJoined(data));
    };
    const onJoinError = ({ message }: { message: string }) => {
      dispatch(setError(message));
    };

    socket.on("room-created", onRoomCreated);
    socket.on("room-joined", onRoomJoined);
    socket.on("join-error", onJoinError);

    return () => {
      socket.off("room-created", onRoomCreated);
      socket.off("room-joined", onRoomJoined);
      socket.off("join-error", onJoinError);
    };
  }, [dispatch]);

  // Navigate to game once we have a role
  useEffect(() => {
    if (role) navigate("/multiplayer/game");
  }, [role, navigate]);

  const effectiveName =
    playerName.trim() ||
    farcasterUser?.displayName ||
    farcasterUser?.username ||
    "Player";

  const handleCreate = () => {
    if (!socketReady) return;
    dispatch(setConnecting(true));
    dispatch(setError(null));
    socket.emit("create-room", {
      playerName: effectiveName,
      fid: farcasterUser?.fid,
      pfpUrl: farcasterUser?.pfpUrl,
      username: farcasterUser?.username,
      playerId: getPlayerId(),
    });
  };

  const handleJoin = () => {
    if (!socketReady || !joinCode.trim()) return;
    dispatch(setConnecting(true));
    dispatch(setError(null));
    socket.emit("join-room", {
      roomId: joinCode.trim().toUpperCase(),
      playerName: effectiveName,
      fid: farcasterUser?.fid,
      pfpUrl: farcasterUser?.pfpUrl,
      username: farcasterUser?.username,
      playerId: getPlayerId(),
      joinAs: joinAs === "spectator" ? "spectator" : undefined,
    });
  };

  const handleCopyCode = () => {
    if (!mpState.roomId) return;
    navigator.clipboard.writeText(mpState.roomId).then(() => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    });
  };

  const handleJoinFromList = (roomId: string, asSpectator: boolean) => {
    if (!socketReady) return;
    dispatch(setConnecting(true));
    dispatch(setError(null));
    socket.emit("join-room", {
      roomId,
      playerName: effectiveName,
      fid: farcasterUser?.fid,
      pfpUrl: farcasterUser?.pfpUrl,
      username: farcasterUser?.username,
      playerId: getPlayerId(),
      joinAs: asSpectator ? "spectator" : undefined,
    });
  };

  // Show the "waiting for opponent" state after creating a room
  const roomCreated =
    mpState.roomId && role === "p1" && mpState.room && !mpState.room.isStarted;

  // Filter rooms: (a) don't show the user's own waiting room (shown above),
  // (b) hide rooms that are already finished — FIX M7 (UX: otherwise the list
  // fills with stale "Finished" badges that users cannot act on).
  const displayRooms = availableRooms.filter((r) => {
    if (roomCreated && r.id === mpState.roomId) return false;
    if (r.winner) return false;
    return true;
  });

  return (
    <Page>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <TopRow>
          <Title>🌐 Multiplayer</Title>
          {farcasterUser && (
            <FarcasterUserBadge>
              <PlayerAvatar
                pfpUrl={farcasterUser.pfpUrl}
                name={
                  farcasterUser.displayName || farcasterUser.username || "?"
                }
                size={28}
                ringColor="#7945ff"
              />
              <FarcasterUserName>
                {farcasterUser.username
                  ? `@${farcasterUser.username}`
                  : farcasterUser.displayName}
              </FarcasterUserName>
            </FarcasterUserBadge>
          )}
        </TopRow>

        {!socketReady && <StatusMsg>Connecting to server…</StatusMsg>}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        {/* Name section */}
        <Section>
          <Subtitle>Your Name</Subtitle>
          <NameInput
            type="text"
            placeholder={
              farcasterUser
                ? farcasterUser.displayName ||
                  farcasterUser.username ||
                  "Enter name"
                : "Enter your name"
            }
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            maxLength={20}
          />
        </Section>

        {/* Room created waiting state */}
        {roomCreated && mpState.roomId ? (
          <CreatedRoomBox>
            <CreatedLabel>Your Room Code</CreatedLabel>
            <CreatedCode>{mpState.roomId}</CreatedCode>
            <div
              style={{
                display: "flex",
                gap: "0.8rem",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <CopyCodeBtn onClick={handleCopyCode}>
                {copiedCode ? "✓ Copied!" : "📋 Copy Code"}
              </CopyCodeBtn>
            </div>
            <ShareInviteBtn
              roomId={mpState.roomId}
              hostName={
                farcasterUser?.displayName ||
                farcasterUser?.username ||
                playerName.trim()
              }
            />
            <WaitText>Waiting for opponent to join…</WaitText>
          </CreatedRoomBox>
        ) : (
          <Section>
            <Subtitle>Create a Room</Subtitle>
            <ActionBtn
              color="purple"
              onClick={handleCreate}
              disabled={!socketReady || isConnecting}
            >
              {isConnecting && role === null ? "Creating…" : "Create Room"}
            </ActionBtn>
          </Section>
        )}

        <Divider />

        {/* Available Rooms Browser */}
        <RoomsSection>
          <SectionHeader>
            <Subtitle>Available Rooms</Subtitle>
            <RefreshBtn onClick={fetchRooms} title="Refresh rooms list">
              {loadingRooms ? "…" : "↻ Refresh"}
            </RefreshBtn>
          </SectionHeader>
          {loadingRooms && displayRooms.length === 0 ? (
            <StatusMsg style={{ fontSize: "1.3rem", padding: "1.2rem 0" }}>
              Loading rooms…
            </StatusMsg>
          ) : displayRooms.length === 0 ? (
            <EmptyRoomsMsg>No active rooms yet. Create one!</EmptyRoomsMsg>
          ) : (
            <RoomsList>
              {displayRooms.map((room) => {
                const status: "waiting" | "playing" | "finished" = room.winner
                  ? "finished"
                  : room.isStarted
                    ? "playing"
                    : "waiting";
                const canJoinAsPlayer = room.playerCount < 2 && !room.winner;
                return (
                  <RoomCard key={room.id}>
                    <RoomCardLeft>
                      <PlayerAvatar
                        pfpUrl={room.hostPfpUrl}
                        name={room.hostName}
                        size={36}
                        ringColor="#7945ff"
                      />
                      <RoomCardInfo>
                        <RoomCardHost>
                          {room.hostName} <RoomCardCode>{room.id}</RoomCardCode>
                        </RoomCardHost>
                        <RoomCardMeta>
                          👥 {room.playerCount}/2 &nbsp;
                          {room.spectatorCount > 0 && (
                            <>👁 {room.spectatorCount} &nbsp;</>
                          )}
                          <RoomStatusBadge status={status}>
                            {status === "waiting"
                              ? "Waiting"
                              : status === "playing"
                                ? "Playing"
                                : "Finished"}
                          </RoomStatusBadge>
                        </RoomCardMeta>
                      </RoomCardInfo>
                    </RoomCardLeft>
                    <RoomCardActions>
                      {farcasterUser?.fid &&
                      room.hostFid === farcasterUser.fid ? (
                        // Host re-entering their own room
                        <RoomJoinBtn
                          variant="join"
                          onClick={() => handleJoinFromList(room.id, false)}
                          disabled={!socketReady || isConnecting}
                          title="Re-enter your room"
                        >
                          ▶ Re-enter
                        </RoomJoinBtn>
                      ) : (
                        <>
                          {canJoinAsPlayer && (
                            <RoomJoinBtn
                              variant="join"
                              onClick={() => handleJoinFromList(room.id, false)}
                              disabled={!socketReady || isConnecting}
                              title="Join as player"
                            >
                              Join
                            </RoomJoinBtn>
                          )}
                          <RoomJoinBtn
                            variant="spectate"
                            onClick={() => handleJoinFromList(room.id, true)}
                            disabled={!socketReady || isConnecting}
                            title="Watch as spectator"
                          >
                            👁 Watch
                          </RoomJoinBtn>
                        </>
                      )}
                    </RoomCardActions>
                  </RoomCard>
                );
              })}
            </RoomsList>
          )}
        </RoomsSection>

        <Divider />

        <Section>
          <Subtitle>Join by Code</Subtitle>
          <JoinTypeRow>
            <JoinTypeBtn
              active={joinAs === "player"}
              onClick={() => setJoinAs("player")}
            >
              🎮 Player
            </JoinTypeBtn>
            <JoinTypeBtn
              active={joinAs === "spectator"}
              onClick={() => setJoinAs("spectator")}
            >
              👁 Spectator
            </JoinTypeBtn>
          </JoinTypeRow>
          <Input
            type="text"
            placeholder="Room Code (e.g. ABC123)"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={6}
          />
          <ActionBtn
            color={joinAs === "spectator" ? "red" : "yellow"}
            onClick={handleJoin}
            disabled={!socketReady || isConnecting || !joinCode.trim()}
          >
            {isConnecting
              ? "Joining…"
              : joinAs === "spectator"
                ? "Watch Room"
                : "Join Room"}
          </ActionBtn>
        </Section>
      </Card>

      <BackBtn onClick={() => navigate("/")}>← Back to Menu</BackBtn>
    </Page>
  );
};

export default MultiplayerLobby;
